import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import { PageMeta, faqJsonLd } from "@/lib/seo";
import { COMPARISONS } from "@/lib/compareData";

function Column({ title, items, accent, testid }) {
  return (
    <div className={`border p-6 sm:p-7 ${accent ? "border-terracotta/60 bg-terracotta/[0.05]" : "border-obsidian-600"}`} data-testid={testid}>
      <p className={`font-sans uppercase tracking-[0.24em] text-[0.65rem] mb-5 ${accent ? "text-terracotta" : "text-bone-400"}`}>{title}</p>
      <ul className="space-y-3">
        {items.map((t, i) => (
          <li key={i} className="flex gap-3 font-sans text-sm sm:text-base text-bone-300 leading-relaxed">
            <Check className={`h-4 w-4 mt-1 shrink-0 ${accent ? "text-terracotta" : "text-bone-500"}`} />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Compare() {
  const { rival } = useParams();
  const c = COMPARISONS[rival];
  if (!c) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-obsidian-900">
      <PageMeta title={c.title} description={c.description} path={`/vs/${rival}`} jsonLd={[faqJsonLd(c.faq)]} />
      <SiteHeader />
      <main className="max-w-5xl mx-auto px-6 sm:px-8 md:px-16 pt-14 sm:pt-24 pb-20">
        <p className="font-sans uppercase tracking-[0.28em] text-xs text-terracotta mb-6">Compare</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-bone-100 mb-8" data-testid="compare-title">
          Umbra <span className="italic text-bone-400">vs</span> {c.name}
        </h1>
        <p className="font-sans text-base sm:text-lg text-bone-300 leading-relaxed max-w-2xl" data-testid="compare-intro">{c.intro}</p>

        <GlanceTable name={c.name} rows={c.glance} />

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
          <Column title="Where Umbra wins" items={c.umbraWins} accent testid="compare-umbra-wins" />
          <Column title={`Where ${c.name} wins`} items={c.rivalWins} testid="compare-rival-wins" />
        </div>

        <section className="mt-20 max-w-3xl" data-testid="compare-faq">
          <h2 className="font-serif text-2xl md:text-3xl text-bone-100 mb-8">Quick questions</h2>
          <dl className="space-y-7">
            {c.faq.map((f, i) => (
              <div key={i}>
                <dt className="font-serif text-xl text-bone-100 mb-2">{f.q}</dt>
                <dd className="font-sans text-sm sm:text-base text-bone-300 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </section>

        <div className="mt-16 flex flex-wrap gap-6 items-center">
          <Link to="/try" data-testid="compare-try-cta" className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-deep text-obsidian-900 px-6 min-h-[48px] font-sans text-xs uppercase tracking-[0.22em] transition-colors">
            Try Chapter One — no account <ArrowRight className="h-4 w-4" />
          </Link>
          {Object.keys(COMPARISONS).filter((k) => k !== rival).map((k) => (
            <Link key={k} to={`/vs/${k}`} data-testid={`compare-link-${k}`} className="font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta">
              Umbra vs {COMPARISONS[k].name} →
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}

function GlanceTable({ name, rows }) {
  return (
    <section className="mt-14" data-testid="compare-glance">
      <h2 className="font-serif text-2xl sm:text-3xl text-bone-100 mb-5">At a glance</h2>
      <dl className="border border-obsidian-600 divide-y divide-obsidian-700">
        {rows.map((r, i) => (
          <div key={r.label} className="grid grid-cols-1 sm:grid-cols-[10rem_1fr_1fr] gap-2 sm:gap-6 p-4" data-testid={`compare-glance-row-${i}`}>
            <dt className="font-sans text-xs uppercase tracking-[0.2em] text-bone-500 sm:pt-0.5">{r.label}</dt>
            <dd className="font-sans text-sm text-bone-100"><span className="sm:hidden text-terracotta">Umbra · </span>{r.umbra}</dd>
            <dd className="font-sans text-sm text-bone-400"><span className="sm:hidden text-bone-500">{name} · </span>{r.rival}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}
